import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useDeferredValue } from 'react';
import { useTranslation } from 'react-i18next';
import { pick } from 'remeda';
import { getListUsersQueryOptions, useListUsersSuspense } from '@/api/generated/endpoints';
import type { User } from '@/api/generated/model';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { DeleteButton } from '@/components/delete-button';
import { PaginationBar } from '@/components/pagination-bar';
import { RouteErrorFallback } from '@/components/route-error-fallback';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TableActions } from '@/components/ui/table';
import { UserFormDrawer, type UserFormDrawerState } from '@/features/users/components/user-form-drawer';
import { UsersFilters } from '@/features/users/components/users-filters';
import { UsersTable } from '@/features/users/components/users-table';
import { useDeleteUserMutation } from '@/features/users/hooks/use-user-mutations';
import { toListUsersParams, usersSearchSchema } from '@/features/users/utils/search';
import { useConfirmAction } from '@/hooks/use-confirm-action';
import { getApiErrorMessage } from '@/utils/errors';

export const Route = createFileRoute('/_authenticated/users')({
	validateSearch: usersSearchSchema,
	loaderDeps: ({ search }) => search,
	loader: ({ context: { queryClient }, deps }) => {
		// Fire-and-forget prefetch: the component reads via a suspense query, so the
		// layout-level <Suspense> shows GenericPageSkeleton until data resolves.
		void queryClient.ensureQueryData(getListUsersQueryOptions(toListUsersParams(deps)));
	},
	errorComponent: (props) => <RouteErrorFallback {...props} fallbackMessageKey='users_error_load' />,
	component: UsersRoute,
});

function UsersRoute() {
	const { t } = useTranslation();
	const search = Route.useSearch();
	const navigate = useNavigate({ from: '/users' });
	const deferredSearch = useDeferredValue(search);
	const isStale = deferredSearch !== search;

	const usersQuery = useListUsersSuspense(toListUsersParams(deferredSearch), {
		query: { retry: false },
	});

	const deleteUser = useConfirmAction<User, { userId: string }>({
		mutation: useDeleteUserMutation(),
		toVariables: (user) => ({ userId: user.id }),
		formatError: (err) => getApiErrorMessage(err, t('error_delete_user')),
	});

	const users = usersQuery.data.data;
	const meta = usersQuery.data.meta;
	const currentPage = search.page;
	const totalPages = meta.total_pages;
	const drawerState: UserFormDrawerState = pick(search, ['mode', 'userId']);
	const drawerOpen = drawerState.mode !== undefined;

	return (
		<>
			<Card>
				<CardHeader className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
					<div>
						<CardTitle>{t('users_page_title')}</CardTitle>
						<CardDescription>{t('users_page_description')}</CardDescription>
					</div>

					<Button asChild>
						<Link to='.' search={(previous) => ({ ...previous, mode: 'create' as const, userId: undefined })}>
							{t('users_create_button')}
						</Link>
					</Button>
				</CardHeader>

				<CardContent className='space-y-4'>
					<UsersFilters
						onApply={() => {
							navigate({
								search: (previous) => ({ ...previous, page: 1 }),
							});
						}}
					/>

					<UsersTable
						users={users}
						isStale={isStale}
						actions={(user) => (
							<TableActions>
								<Button asChild variant='outline' size='sm'>
									<Link
										to='.'
										search={(previous) => ({
											...previous,
											mode: 'edit' as const,
											userId: user.id,
										})}
									>
										{t('users_edit_button')}
									</Link>
								</Button>
								<DeleteButton onClick={() => deleteUser.request(user)} />
							</TableActions>
						)}
					/>

					<PaginationBar
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={(page) => navigate({ search: (prev) => ({ ...prev, page }) })}
					/>
				</CardContent>
			</Card>

			<UserFormDrawer
				{...drawerState}
				open={drawerOpen}
				onOpenChange={(isOpen) => {
					if (!isOpen) {
						navigate({
							search: (previous) => ({
								...previous,
								mode: undefined,
								userId: undefined,
							}),
						});
					}
				}}
			/>

			<ConfirmDeleteDialog
				{...deleteUser.dialogProps}
				title={t('users_delete_title')}
				description={t('users_delete_description', {
					name: deleteUser.target?.name ?? t('users_delete_fallback_name'),
				})}
			/>
		</>
	);
}
